import { ClassNames } from '@emotion/core'
import fp from 'lodash/fp'
import React, { useEffect, useRef } from 'react'
import PropTypes from 'prop-types'
import ReactTable, { ReactTableDefaults } from 'react-table'

import { Pagination } from './components/pagination'
import { TableState } from './components/tableState'
import { DefaultCell } from './cellRenderers/default.cell'
import * as styles from './styles'

ReactTableDefaults.column.Cell = DefaultCell

const buildSkeletonNumbers = (count) =>
  Array.from({ length: count }, (_, index) => index + 1)

const SkeletonRows = ({ columnCount, rowCount }) => {
  const rowNumbers = buildSkeletonNumbers(rowCount)
  const columnNumbers = buildSkeletonNumbers(columnCount)

  return (
    <ClassNames>
      {({ css, cx }) =>
        rowNumbers.map((rowNumber) => (
          <div
            key={`skeleton-row-${rowNumber}`}
            className={css(styles.skeletonRow(columnCount))}
          >
            {columnNumbers.map((columnNumber) => (
              <div
                key={`skeleton-cell-${rowNumber}-${columnNumber}`}
                className={cx(
                  css(styles.skeletonCell),
                  columnNumber === 1 && css(styles.skeletonCellPrimary)
                )}
              />
            ))}
          </div>
        ))
      }
    </ClassNames>
  )
}

SkeletonRows.propTypes = {
  columnCount: PropTypes.number.isRequired,
  rowCount: PropTypes.number.isRequired,
}

const actionColumnPattern = /(control|update|invoice|inwin)/i

const NoDataComponent = ({ loading, error, columnCount }) => {
  if (loading) {
    return <TableState variant="loading" columnCount={columnCount} />
  }

  if (error) {
    return (
      <TableState
        variant="error"
        title="Could not load rows"
        description="Refresh the page or try again in a moment."
      />
    )
  }

  return (
    <TableState
      variant="empty"
      title="No rows found"
      description="Try adjusting the current filters."
    />
  )
}

NoDataComponent.propTypes = {
  loading: PropTypes.bool,
  error: PropTypes.bool,
  columnCount: PropTypes.number,
}

const PaginationComponent = ({
  tableState,
  forcePage,
  onPageChange,
  ...paginationProps
}) => {
  return (
    <Pagination
      {...paginationProps}
      tableState={tableState}
      forcePage={forcePage}
      onPageChange={onPageChange}
    />
  )
}

PaginationComponent.propTypes = {
  tableState: PropTypes.shape(),
  forcePage: PropTypes.number,
  onPageChange: PropTypes.func,
}

const EmptyNoDataComponent = () => null

const decorateColumns = (columns) =>
  columns.map((column, index) => {
    const columnKey = String(column.id || column.Header || '')
    const isActionColumn = actionColumnPattern.test(columnKey)

    return {
      ...column,
      headerClassName: [
        column.headerClassName,
        index === 0 && 'inwin-table__header--primary',
        isActionColumn && 'inwin-table__header--action',
      ]
        .filter(Boolean)
        .join(' '),
      className: [
        column.className,
        index === 0 && 'inwin-table__cell--primary',
        isActionColumn && 'inwin-table__cell--action',
      ]
        .filter(Boolean)
        .join(' '),
    }
  })

const TableComponent = ({
  id,
  tableRef,
  fetchResult,
  columns,
  loading,
  onFetchData,
  className,
  defaultPageSize = 15,
  pageSizeOptions = [10, 15, 20, 25, 50],
  showPaginationTop = false,
  showPaginationBottom = true,
  showPageSizeOptions = true,
  resizable = true,
  sortable = false,
  noPagination = false,
  currentPage = 0,
  setCurrentPage,
  ...props
}) => {
  const isWithoutPagination = noPagination
  const totalPages = fp.getOr(1, 'totalPages', fetchResult)
  const data = fp.getOr([], 'content', fetchResult)
  const isEmpty = !loading && data.length === 0
  const isError = !loading && !!fp.get('error', fetchResult)
  const decoratedColumns = decorateColumns(columns)

  // Track when sportType changes in fetchResult
  const currentSportType = fp.getOr(null, 'sportType', fetchResult)
  const prevSportTypeRef = useRef(null)

  // Create a ref to track previous filter state
  const prevFilteredRef = useRef(null)

  // When sport type changes, reset page to 0
  useEffect(() => {
    if (
      prevSportTypeRef.current !== null &&
      prevSportTypeRef.current !== currentSportType &&
      currentSportType !== null
    ) {
      // Reset page in component state
      if (setCurrentPage) {
        setCurrentPage(0)
      }

      // Reset page in table state
      if (tableRef.current) {
        tableRef.current.setState({ page: 0 }, () => {
          // Force a re-fetch with the updated page
          if (tableRef.current.state) {
            const newState = { ...tableRef.current.state, page: 0 }
            onFetchData(newState)
          }
        })
      }
    }

    // Update the previous sport type
    prevSportTypeRef.current = currentSportType
  }, [currentSportType, setCurrentPage, onFetchData])

  // Use a ref to track previous total pages for comparison
  const prevTotalPagesRef = useRef(totalPages)

  // When total pages changes, ensure the page is reset if needed
  useEffect(() => {
    if (tableRef.current && prevTotalPagesRef.current !== totalPages) {
      // If current page is out of bounds after total pages changed
      if (currentPage >= totalPages && totalPages > 0) {
        const newPage = Math.max(0, totalPages - 1)

        // First update our container state
        if (setCurrentPage) {
          setCurrentPage(newPage)
        }

        // Then update table state and trigger re-fetch
        tableRef.current.setState(
          {
            page: newPage,
          },
          () => {
            // Force a re-fetch with the updated page
            if (tableRef.current?.state) {
              onFetchData(tableRef.current.state)
            }
          }
        )
      }

      // Update ref
      prevTotalPagesRef.current = totalPages
    }
  }, [totalPages, tableRef, setCurrentPage, currentPage, onFetchData])

  // Track when search or filter changes in table state
  useEffect(() => {
    if (tableRef.current?.state) {
      const { filtered } = tableRef.current.state

      // If we have active filters
      if (filtered && filtered.length > 0) {
        const currentFilteredStr = JSON.stringify(filtered)

        // Only reset page if filters have actually changed
        if (prevFilteredRef.current !== currentFilteredStr) {
          // Always reset to page 0 when filters change - we weren't doing this consistently
          if (setCurrentPage) {
            setCurrentPage(0)
          }

          // Update the table state
          tableRef.current.setState({ page: 0 }, () => {
            // Trigger a re-fetch with page 0
            if (tableRef.current.state) {
              const newState = { ...tableRef.current.state, page: 0 }
              onFetchData(newState)
            }
          })

          // Store current filter state for next comparison
          prevFilteredRef.current = currentFilteredStr
        }
      } else {
        // Reset the previous filtered ref if there are no filters
        prevFilteredRef.current = null
      }
    }
  }, [tableRef.current?.state?.filtered, setCurrentPage, onFetchData])

  // Handle page change from pagination control
  const handlePageChange = (pageIndex, filtered) => {
    // Ensure pageIndex is treated as a number
    const numPageIndex = Number(pageIndex)

    // Make sure we're not navigating to an invalid page
    const safePageIndex = Math.max(0, Math.min(numPageIndex, totalPages - 1))

    // Update our state immediately
    if (setCurrentPage) {
      setCurrentPage(safePageIndex)
    }

    // Update table ref state
    if (tableRef.current) {
      const newState = { page: safePageIndex }

      // Include filtered state if provided
      if (filtered) {
        newState.filtered = filtered
      } else if (tableRef.current.state?.filtered) {
        newState.filtered = tableRef.current.state.filtered
      }

      tableRef.current.setState(newState, () => {
        // Force a re-fetch with the updated page and filters
        if (tableRef.current.state) {
          onFetchData(tableRef.current.state)
        }
      })
    }

    // Then call the original handler if provided
    if (props.onPageChange) {
      props.onPageChange({ selected: safePageIndex }, filtered)
    }
  }

  return (
    <div css={styles.wrapper}>
      <ReactTable
        id={id}
        ref={tableRef}
        data={isEmpty ? [] : data}
        loading={loading}
        columns={decoratedColumns}
        totalPages={totalPages}
        manual={true}
        sortable={sortable}
        showPaginationTop={false}
        showPaginationBottom={false}
        resizable={resizable}
        showPagination={false}
        showPageSizeOptions={false}
        defaultPageSize={isEmpty ? 1 : defaultPageSize}
        className={['-striped', '-highlight', className].join(' ')}
        getNoDataProps={() => ({
          loading,
          error: isError,
          columnCount: decoratedColumns.length,
        })}
        NoDataComponent={NoDataComponent}
        minRows={isEmpty ? 0 : 1}
        onFetchData={onFetchData}
        page={currentPage} // Use explicitly tracked page from container
        {...props}
      />
      {!isWithoutPagination && (
        <ReactTable
          id={`${id}-pagination`}
          data={data}
          columns={decoratedColumns}
          totalPages={totalPages}
          manual={true}
          showPaginationTop={showPaginationTop}
          showPaginationBottom={showPaginationBottom}
          showPagination={true}
          showPageSizeOptions={showPageSizeOptions}
          defaultPageSize={defaultPageSize}
          pageSizeOptions={pageSizeOptions}
          onFetchData={onFetchData}
          page={currentPage} // Use explicitly tracked page from container
          onPageChange={handlePageChange} // Handle page changes
          getPaginationProps={() => ({
            tableState: tableRef.current?.state,
            forcePage: currentPage, // Always force the page to our explicitly tracked value
            onPageChange: handlePageChange,
          })}
          PaginationComponent={PaginationComponent}
          className="pagination-only"
          NoDataComponent={EmptyNoDataComponent}
          style={{
            height: 'auto',
            minHeight: 0,
            border: 'none',
          }}
          minRows={0}
        />
      )}
    </div>
  )
}

TableComponent.propTypes = {
  id: PropTypes.string.isRequired,
  tableRef: PropTypes.shape(),
  loading: PropTypes.bool,
  fetchResult: PropTypes.shape(),
  className: PropTypes.string,
  totalPages: PropTypes.number,
  defaultPageSize: PropTypes.number,
  columns: PropTypes.arrayOf(PropTypes.shape()),
  pageSizeOptions: PropTypes.arrayOf(PropTypes.number),
  resizable: PropTypes.bool,
  onFetchData: PropTypes.func.isRequired,
  showPaginationTop: PropTypes.bool,
  showPaginationBottom: PropTypes.bool,
  showPageSizeOptions: PropTypes.bool,
  noPagination: PropTypes.bool,
  sortable: PropTypes.bool,
  onPageChange: PropTypes.func,
  currentPage: PropTypes.number,
  setCurrentPage: PropTypes.func,
}

export default TableComponent
